import styled, { css } from 'styled-components';
import { colors, fontFamily, SIDEBAR_WIDTH } from '../../Utils/tokens';

export const SidebarContainer = styled.aside`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${SIDEBAR_WIDTH}px;
  height: 100%;
  overflow: hidden;
  background-color: ${colors.surfaceContainer};
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
`;

export const AiTitle = styled.div`
  display: flex;
  flex: 1 0 0;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 4px 6px;

  img {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }

  span {
    overflow: hidden;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: ${colors.onSurface};
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ScrollArea = styled.div`
  position: relative;
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  min-height: 0;
`;

export const SidebarContent = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding: 0 12px 48px;
  overflow-x: hidden;
  overflow-y: auto;
`;

export const BottomGradient = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 48px;
  background: linear-gradient(to top, ${colors.surfaceContainer}, rgba(247, 247, 247, 0));
  pointer-events: none;
`;

export const TopActions = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  gap: 6px;
  width: 100%;
`;

export const NewChatButton = styled.button`
  display: flex;
  flex: 1 0 0;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  padding: 7px 11px;
  border: 1px solid ${colors.surfaceOutline};
  border-radius: 6px;
  background-color: ${colors.surface};
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${colors.onSurface};
  cursor: pointer;
  transition: background-color 0.15s ease;

  img {
    width: 16px;
    height: 16px;
  }

  &:hover,
  &:focus-visible {
    background-color: ${colors.secondary};
    outline: none;
  }
`;

export const SearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background-color: ${colors.secondary};
  cursor: pointer;
  transition: background-color 0.15s ease;

  img {
    width: 16px;
    height: 16px;
  }

  &:hover,
  &:focus-visible {
    background-color: ${colors.surfaceOutline};
    filter: brightness(0.95);
    outline: none;
  }
`;

export const SearchField = styled.label<{ $focused: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  width: 100%;
  padding: 7px 7px 7px 5px;
  border: 1px solid ${({ $focused }) => ($focused ? colors.primary : 'transparent')};
  border-radius: 6px;
  background-color: ${({ $focused }) => ($focused ? colors.surface : colors.secondary)};
  cursor: text;

  > img {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }

  input {
    flex: 1 0 0;
    min-width: 0;
    padding: 0;
    border: none;
    outline: none;
    background: transparent;
    font-family: ${fontFamily};
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: ${colors.onSecondary};

    &::placeholder {
      color: ${colors.onSurfacePlaceholder};
    }
  }

  button {
    display: flex;
    flex-shrink: 0;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;

    img {
      width: 16px;
      height: 16px;
    }
  }
`;

export const Divider = styled.div`
  flex-shrink: 0;
  width: 100%;
  padding: 1.5px 0;

  &::after {
    content: '';
    display: block;
    height: 1px;
    background-color: ${colors.surfaceOutline};
  }
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 6px;
  width: 100%;
`;

export const GroupTitle = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  height: 14px;
  padding: 0 6px;
  background-color: ${colors.surfaceContainer};
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  color: ${colors.onSurfaceVariant};
`;

export const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

export const SpecialistItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 28px;
  padding: 6px;
  border: none;
  border-radius: 6px;
  background-color: ${({ $active }) => ($active ? colors.secondary : 'transparent')};
  cursor: pointer;
  text-align: left;

  > img {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }

  span {
    flex: 1 0 0;
    min-width: 0;
    overflow: hidden;
    font-family: ${fontFamily};
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: ${colors.onSurface};
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover,
  &:focus-visible {
    background-color: ${colors.onSurfaceHover};
    outline: none;
  }
`;

export const SpecialistPersona = styled.img`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 8px;
  object-fit: cover;
`;

export const History = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const centeredState = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 12px 0;
  text-align: center;
`;

export const EmptyHistory = styled.div`
  ${centeredState};
  gap: 6px;

  img {
    width: 24px;
    height: 24px;
  }

  span {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: ${colors.onSurfaceVariant};
  }
`;

export const NoResults = styled.div`
  ${centeredState};
  gap: 12px;
`;

export const NoResultsIcon = styled.div`
  display: flex;
  padding: 10px;
  border-radius: 22px;
  background-color: ${colors.secondary};

  img {
    width: 24px;
    height: 24px;
  }
`;

export const NoResultsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  font-size: 12px;
  line-height: normal;

  strong {
    font-weight: 600;
    color: ${colors.onSurface};
  }

  span {
    font-weight: 400;
    color: ${colors.onSurfaceVariant};
  }
`;

export const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 12px 0;
`;
